// Port of ios/Sources/ViewModels/UndeliveredBanner.swift.
//
// Whether a conversation owes the reader a word about messages that did not go out — and whether
// it may offer to send them all again.
//
// "All delivered, not delivered should be an option to retry" (user, 2026-09-12). Per-message
// retry already existed and is the right tool for ONE message; it is the wrong tool for the case
// this is for, which is a conversation written on a dead link: eight messages, eight taps, and no
// single place that says how many are outstanding.
//
// The distinction that makes this honest is pending versus failed:
//
//  - **pending** is in flight or waiting for the outbox's own backoff. Presenting it as a failure
//    invites a tap that does nothing but restart a timer, and the outbox is already handling it.
//  - **failed** is where the app has stopped trying on its own. That is the only state a human
//    can improve, so it is the only state that gets a button.
//
// Retry alone was a dead end (user, with a screenshot, 2026-09-12): the bar named a failure it gave
// no way to SEE and no way to be RID of. A message that will never send had exactly one button, and
// it was the button that does not work. So the bar carries all three of the things a person can
// actually want here — look at it, send it again, or throw it away.
//
// Kotlin twin: `UndeliveredBanner` in `UndeliveredBanner.kt`.

/**
 * The bar's whole state. The Swift struct's computed properties (`isVisible`, `canRetry`,
 * `canView`, `canDiscard`) are plain fields here, filled by `decide`, so a view reads them
 * directly and a `toEqual` compares them too.
 */
export interface UndeliveredBannerState {
  /** How many messages have given up and are waiting for a human. */
  failedCount: number;
  /** How many are still on their own way out — reassurance, never an error. */
  pendingCount: number;
  /** True while a retry-all is running, so the button can say so and refuse a second tap. */
  isRetrying: boolean;
  isVisible: boolean;
  canRetry: boolean;
  /**
   * Jumping to the stuck message. Allowed even mid-retry: reading is never destructive, and
   * watching the message you are waiting on is the most natural thing to do while it sends.
   */
  canView: boolean;
  /**
   * Throwing the stuck messages away, which is LOCAL and IRREVERSIBLE — the text lives
   * nowhere else, so this is the one action here that can lose something.
   *
   * Refused while a retry runs, and that is not caution for its own sake: a send already in
   * flight can still be accepted by the server. Deleting the row underneath it would either
   * lose a message the server took, or race the outbox into resurrecting the row we just
   * deleted. Wait for the retry to settle, then the choice is real.
   */
  canDiscard: boolean;
}

function decide(failedCount: number, pendingCount: number, isRetrying = false): UndeliveredBannerState {
  const failed = Math.max(0, failedCount);
  return {
    failedCount: failed,
    pendingCount: Math.max(0, pendingCount),
    isRetrying,
    isVisible: failed > 0,
    canRetry: failed > 0 && !isRetrying,
    canView: failed > 0,
    canDiscard: failed > 0 && !isRetrying,
  };
}

export const UndeliveredBanner = {
  decide,
} as const;
